import { useCallback, useEffect, useState } from "react";

const MAX_COOLDOWN_SECONDS = 3600;

export function getRemainingCooldown(key: string | null): number {
  if (!key) return 0;
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return 0;
    const expiresAt = Number(raw);
    if (!Number.isFinite(expiresAt)) {
      localStorage.removeItem(key);
      return 0;
    }
    const remaining = Math.ceil((expiresAt - Date.now()) / 1000);

    if (remaining <= 0) {
      localStorage.removeItem(key);
      return 0;
    }

    if (remaining > MAX_COOLDOWN_SECONDS) {
      localStorage.removeItem(key);
      return 0;
    }
    return remaining;
  } catch {
    return 0;
  }
}

export function startCooldown(key: string | null, seconds: number = 60): void {
  if (!key) return;
  const expiresAt = Date.now() + seconds * 1000;
  try {
    localStorage.setItem(key, expiresAt.toString());
  } catch {}
  window.dispatchEvent(new CustomEvent("cooldown_updated", { detail: { key } }));
}

export function clearCooldown(key: string | null): void {
  if (!key) return;
  try {
    localStorage.removeItem(key);
  } catch {}
  window.dispatchEvent(new CustomEvent("cooldown_updated", { detail: { key } }));
}

export function useCooldown(
  key: string | null,
  defaultSeconds: number = 60
) {
  const [cooldown, setCooldown] = useState<number>(() =>
    getRemainingCooldown(key)
  );

  useEffect(() => {
    setCooldown(getRemainingCooldown(key));
  }, [key]);

  useEffect(() => {
    if (!key) return;

    const handleUpdate = (e: Event) => {
      const customEvent = e as CustomEvent<{ key?: string }>;
      if (!customEvent.detail?.key || customEvent.detail.key === key) {
        setCooldown(getRemainingCooldown(key));
      }
    };

    const handleStorage = (e: StorageEvent) => {
      if (!e.key || e.key === key) {
        setCooldown(getRemainingCooldown(key));
      }
    };

    window.addEventListener("cooldown_updated", handleUpdate);
    window.addEventListener("storage", handleStorage);

    return () => {
      window.removeEventListener("cooldown_updated", handleUpdate);
      window.removeEventListener("storage", handleStorage);
    };
  }, [key]);

  const isRunning = cooldown > 0;

  useEffect(() => {
    if (!isRunning || !key) return;

    const interval = setInterval(() => {
      const remaining = getRemainingCooldown(key);
      setCooldown(remaining);
      if (remaining <= 0) {
        clearInterval(interval);
        clearCooldown(key);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [isRunning, key]);

  const triggerStart = useCallback(
    (seconds: number = defaultSeconds) => {
      if (!key) return;
      startCooldown(key, seconds);
      setCooldown(seconds);
    },
    [key, defaultSeconds]
  );

  return {
    cooldown,
    startCooldown: triggerStart,
    clearCooldown: useCallback(() => clearCooldown(key), [key]),
  };
}

export default useCooldown;
